import React, { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/firebase-config";
import { createUpgradeLine, normalizeUpgradeLine, PUBLIC_UPGRADE_LINE_SCOPE } from "../services/upgradeLines";
import "./UpgradeLine.css";

const emptyForm = () => ({ number: "", noOfLines: "", lastPaid: "", remarks: "" });

const UpgradeLineSubmission = ({ userId }) => {
  const fieldId = useId();
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const updateField = event => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
    setFieldErrors(current => ({ ...current, [name]: "" }));
    setError("");
    setSuccess("");
  };

  const submit = async event => {
    event.preventDefault();
    if (inFlight.current) return;
    if (auth.currentUser?.uid !== userId || auth.currentUser?.isAnonymous !== false) {
      setError("Your sign-in changed. Please wait for your account to update before submitting.");
      return;
    }
    setError("");
    setSuccess("");
    try {
      normalizeUpgradeLine(form);
    } catch (failure) {
      setFieldErrors(failure.fieldErrors || {});
      if (!failure.fieldErrors) setError(failure.message || "Check your details and try again.");
      if (failure.field) event.currentTarget.elements.namedItem(failure.field)?.focus();
      return;
    }

    inFlight.current = true;
    setSaving(true);
    setFieldErrors({});
    try {
      await createUpgradeLine(PUBLIC_UPGRADE_LINE_SCOPE, form);
      if (!mounted.current) return;
      setForm(emptyForm());
      setSuccess("Upgrade line submitted for admin review.");
    } catch {
      if (mounted.current) setError("Your upgrade line could not be submitted. Your details are still here. Please try again.");
    } finally {
      inFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const inputProps = name => ({
    id: `${fieldId}-${name}`,
    name,
    value: form[name],
    onChange: updateField,
    disabled: saving,
    "aria-invalid": fieldErrors[name] ? "true" : undefined,
    "aria-describedby": [name === "lastPaid" ? `${fieldId}-amount-help` : "", fieldErrors[name] ? `${fieldId}-${name}-error` : ""].filter(Boolean).join(" ") || undefined,
  });
  const fieldError = name => fieldErrors[name]
    ? <span className="upgrade-line-field-error" id={`${fieldId}-${name}-error`}>{fieldErrors[name]}</span>
    : null;

  return <main className="upgrade-line-page upgrade-line-public" aria-labelledby={`${fieldId}-title`}>
    <header className="upgrade-line-heading">
      <h1 id={`${fieldId}-title`}>Upgrade Line</h1>
      <p>Submit a number and its payment details for admin review. Submissions are private and can only be viewed and managed by the admin.</p>
    </header>
    <div className="upgrade-line-card">
      <h2>Submit upgrade line</h2>
      <form className="upgrade-line-form" onSubmit={submit} noValidate aria-busy={saving}>
        <div className="upgrade-line-fields">
          <div className="upgrade-line-field">
            <label htmlFor={`${fieldId}-number`}>Number <span aria-hidden="true">*</span></label>
            <input {...inputProps("number")} type="tel" autoComplete="tel" placeholder="Enter phone number" required />
            {fieldError("number")}
          </div>
          <div className="upgrade-line-field">
            <label htmlFor={`${fieldId}-noOfLines`}>No. of Lines <span aria-hidden="true">*</span></label>
            <input {...inputProps("noOfLines")} type="text" inputMode="numeric" placeholder="Enter number of lines" required />
            {fieldError("noOfLines")}
          </div>
          <div className="upgrade-line-field">
            <label htmlFor={`${fieldId}-lastPaid`}>Last Paid <span aria-hidden="true">*</span></label>
            <input {...inputProps("lastPaid")} type="text" inputMode="decimal" placeholder="0.00" required />
            <span className="upgrade-line-help" id={`${fieldId}-amount-help`}>Amount, up to 2 decimal places.</span>
            {fieldError("lastPaid")}
          </div>
          <div className="upgrade-line-field upgrade-line-remarks-field">
            <label htmlFor={`${fieldId}-remarks`}>Remarks <span className="upgrade-line-optional">(optional)</span></label>
            <textarea {...inputProps("remarks")} rows={3} maxLength={2000} placeholder="Add any notes" />
            {fieldError("remarks")}
          </div>
        </div>
        {Object.values(fieldErrors).some(Boolean) && <p className="upgrade-line-error" role="alert">Check the highlighted fields.</p>}
        {error && <p className="upgrade-line-error" role="alert">{error}</p>}
        <div className="upgrade-line-form-footer">
          <button className="upgrade-line-primary" type="submit" disabled={saving}>{saving ? "Submitting…" : "Submit Upgrade Line"}</button>
          <span className="upgrade-line-success" role="status">{success}</span>
        </div>
      </form>
    </div>
  </main>;
};

const UpgradeLineEntry = () => {
  const fieldId = useId();
  const [userId, setUserId] = useState(undefined);
  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (active) setUserId(user?.uid && user.isAnonymous === false ? user.uid : null);
    }, () => { if (active) setUserId(null); });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  if (userId) return <UpgradeLineSubmission key={userId} userId={userId} />;
  return <main className="upgrade-line-page upgrade-line-public" aria-labelledby={`${fieldId}-title`}>
    <header className="upgrade-line-heading"><h1 id={`${fieldId}-title`}>Upgrade Line</h1></header>
    {userId === undefined ? <p className="upgrade-line-access-message" role="status">Checking sign-in…</p>
      : <section className="upgrade-line-card">
        <h2>Sign in to submit an Upgrade Line</h2>
        <p>Use your registered account to submit payment details for admin review. Submissions are private.</p>
        <Link className="upgrade-line-primary upgrade-line-sign-in" to="/login" state={{ returnTo: "/upgrade-line" }}>Sign in</Link>
      </section>}
  </main>;
};

export default UpgradeLineEntry;
